import { Button } from '@/components/ui/button'
import { Sparkles } from 'lucide-react'
import { ACTIVITY_RANGES, type SeverityFilter } from '@/lib/activity-utils'

interface ActivityFiltersProps {
  agents: string[]
  agent: string
  onAgent: (value: string) => void
  severity: SeverityFilter
  onSeverity: (value: SeverityFilter) => void
  rangeKey: string
  onRange: (value: string) => void
  highlightsOnly: boolean
  onToggleHighlights: () => void
  shown: number
  total: number
}

const SEVERITIES: SeverityFilter[] = ['ALL', 'SUCCESS', 'FAILED']

export function ActivityFilters({
  agents,
  agent,
  onAgent,
  severity,
  onSeverity,
  rangeKey,
  onRange,
  highlightsOnly,
  onToggleHighlights,
  shown,
  total,
}: ActivityFiltersProps) {
  return (
    <div className="space-y-2 bg-cmd-container p-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {/* Agent */}
        <label className="flex items-center gap-2">
          <span className="font-label text-[0.6rem] tracking-widest text-cmd-outline uppercase">
            Agent
          </span>
          <select
            value={agent}
            onChange={(e) => onAgent(e.target.value)}
            className="bg-cmd-low text-xs text-white/90 font-label px-2 py-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          >
            <option value="ALL">ALL AGENTS</option>
            {agents.map((a) => (
              <option key={a} value={a}>
                {a}
              </option>
            ))}
          </select>
        </label>

        {/* Severity */}
        <div className="flex items-center gap-1">
          <span className="font-label text-[0.6rem] tracking-widest text-cmd-outline uppercase mr-1">
            Severity
          </span>
          {SEVERITIES.map((s) => (
            <Button
              key={s}
              size="sm"
              variant={severity === s ? 'default' : 'ghost'}
              onClick={() => onSeverity(s)}
              className="font-label text-[0.65rem] tracking-wider"
            >
              {s}
            </Button>
          ))}
        </div>

        {/* Time range */}
        <div className="flex items-center gap-1">
          <span className="font-label text-[0.6rem] tracking-widest text-cmd-outline uppercase mr-1">
            Range
          </span>
          {ACTIVITY_RANGES.map((r) => (
            <Button
              key={r.key}
              size="sm"
              variant={rangeKey === r.key ? 'default' : 'ghost'}
              onClick={() => onRange(r.key)}
              className="font-label text-[0.65rem] tracking-wider"
            >
              {r.label}
            </Button>
          ))}
        </div>

        {/* Highlights only */}
        <Button
          size="sm"
          variant={highlightsOnly ? 'default' : 'outline'}
          onClick={onToggleHighlights}
          aria-pressed={highlightsOnly}
          className="font-label text-[0.65rem] tracking-wider ml-auto"
        >
          <Sparkles className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          HIGHLIGHTS ONLY
        </Button>
      </div>

      <p className="font-mono text-[0.6rem] text-cmd-outline tabular-nums">
        SHOWING {shown} / {total}
      </p>
    </div>
  )
}
